import React, { useCallback, useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
    calculateEMI,
    calculateLoanAmount,
    LTV_CAP,
    percentToValue,
    valueToPercent,
} from "../../../utils";
import useTranslationHook from "../../hooks/useTranslationHook";
import { Trans } from "react-i18next";
import { useSelector } from "react-redux";
import { formatNumber, formatNumberByLocale } from "../../../helpers";

const MortgageCalculator = ({
    buyerType = "expat",
    price = 2400000,
    interest = 3.9,
    downPayment = 50,
    tenure = 15,
    setCalculatorField,
}) => {
    const { t } = useTranslationHook();
    let lang = useSelector((state) => state.translation.lang);


    const { register, control, setValue } = useForm({
        defaultValues: {
            buyerType,
            price,
            interest,
            downPayment,
            tenure,
        },
    });

    const {
        buyerType: currentBuyerType,
        price: currentPrice = 0,
        interest: currentInterest = 0,
        downPayment: currentDownPayment = 0,
        tenure: currentTenure = 0,
    } = useWatch({ control });

    useEffect(() => {
        setCalculatorField?.("buyerType", currentBuyerType);
        setCalculatorField?.("price", currentPrice);
        setCalculatorField?.("interest", currentInterest);
        setCalculatorField?.("downPayment", currentDownPayment);
        setCalculatorField?.("tenure", currentTenure);
    }, [
        currentBuyerType,
        currentPrice,
        currentInterest,
        currentDownPayment,
        currentTenure,
        setCalculatorField,
    ]);

    const handleNumberChange = useCallback(
        (field, min = 0) =>
            (e) => {
                setValue(field, Math.max(min, Number(e.target.value) || min), {
                    shouldDirty: true,
                });
            },
        [setValue]
    );

    const handleRangeChange = useCallback(
        (field, transform = Number) =>
            (e) => {
                setValue(field, transform(e.target.value), {
                    shouldDirty: true,
                });
            },
        [setValue]
    );

    const handleBuyerTypeChange = useCallback(
        (value) => {
            setValue("buyerType", value, { shouldDirty: true });
            setValue("downPayment", LTV_CAP[value], { shouldDirty: true });
        },
        [setValue]
    );

    const loanAmount = useMemo(
        () =>
            calculateLoanAmount(
                currentPrice,
                currentDownPayment,
                currentBuyerType
            ),
        [currentPrice, currentDownPayment, currentBuyerType]
    );

    const emi = useMemo(
        () => calculateEMI(loanAmount, currentInterest, currentTenure),
        [loanAmount, currentInterest, currentTenure]
    );

    const totalPayable = useMemo(
        () => emi * currentTenure * 12,
        [emi, currentTenure]
    );

    const usedLtvPercent = useMemo(
        () => Math.round((loanAmount / currentPrice) * 100),
        [loanAmount, currentPrice]
    );

    const tenurePercent = useMemo(
        () => valueToPercent(currentTenure),
        [currentTenure]
    );

    return (
        <div className="mortage-calculator space">
            <h5>{t("Mortgage Calculator")}</h5>

            <div className="mb-4">
                <label>{t("Buyer Type")}</label>
                <ul className="buyer-tags">
                    {[
                        { value: "uae", label: "UAE National" },
                        { value: "expat", label: "Expat Resident" },
                        { value: "nonResident", label: "Non-Resident" },
                    ].map(({ value, label }) => (
                        <li key={value}>
                            <input
                                type="radio"
                                id={`buyerType-${value}`}
                                value={value}
                                className="css-radio"
                                {...register("buyerType")}
                                onChange={() => handleBuyerTypeChange(value)}
                            />
                            <label
                                htmlFor={`buyerType-${value}`}
                                className="css-labell"
                            >
                                {t(label)}
                            </label>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="row">
                <div className="col-lg-6">
                    <div className="mb-4">
                        <label>{t("Purchase Price")}</label>
                        <div className="price">
                            <span className="icon">
                                <img
                                    src="img/currency-symbol.svg"
                                    alt="Currency symbol"
                                />
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                value={formatNumberByLocale(currentPrice, lang)}
                                onChange={(e) => {
                                    const raw = e.target.value.replace(/[^0-9]/g, ""); // remove commas & junk
                                    setValue("price", raw === "" ? 0 : Number(raw), {
                                        shouldDirty: true,
                                    });
                                }}
                                onBlur={(e) => {
                                    const raw = e.target.value.replace(/[^0-9]/g, "");
                                    setValue("price", raw === "" ? 0 : Number(raw));
                                }}
                                inputMode="numeric"
                            />
                        </div>
                    </div>
                </div>

                <div className="col-lg-6">
                    <div className="mb-4">
                        <label>{t("Interest Rate")}</label>
                        <div className="price">
                            <span className="icon">%</span>
                            <input
                                type="number"
                                step="0.1"
                                className="form-control"
                                value={currentInterest}
                                onChange={handleNumberChange("interest")}
                                onBlur={(e) => {
                                    e.target.value =
                                        Number(e.target.value) || 0;
                                }}
                                onKeyDown={(e) =>
                                    e.key.length === 1 &&
                                    /[eE+\-,]/.test(e.key) &&
                                    e.preventDefault()
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="calculator">
                <div className="slider-section">
                    <div className="label-wrapper">
                        <label>{t("Down Payment")}</label>
                        {/* <span className="amount">
                            {currentDownPayment}%
                        </span> */}
                    </div>

                    <input
                        type="range"
                        min="20"
                        max="100"
                        value={currentDownPayment}
                        onChange={handleRangeChange("downPayment")}
                        style={{
                            "--fill-percent": `${((currentDownPayment - 20) / (100 - 20)) * 100}%`,
                        }}
                    />

                    <div className="range-values">
                        {[20, 40, 60, 80, 100].map((v) => (
                            <span key={v}>{v}%</span>
                        ))}
                    </div>
                </div>

                <div className="slider-section">
                    <div className="label-wrapper">
                        <label>{t("Loan Duration")}</label>
                        {/* <span className="amount">
                            {currentTenure} years
                        </span> */}
                    </div>

                    <input
                        type="range"
                        min="1"
                        max="100"
                        value={tenurePercent}
                        onChange={handleRangeChange("tenure", percentToValue)}
                        style={{
                            "--fill-percent": `${tenurePercent}%`,
                        }}
                    />

                    <div className="range-values">
                        {[1, 20, 25].map((v) => (
                            <span key={v}>{v}</span>
                        ))}
                    </div>
                </div>
            </div>

            <div className="mb-4">
                <label>{t("Total")}</label>
                <div className="total">
                    <div className="block">
                        <div className="emi-result">
                            <h6>
                                <span>
                                    {t("Monthly EMI")}
                                </span>
                                {/* <em className="dirham">D</em>{" "}
                                {Math.round(emi).toLocaleString()} */}
                                {formatNumber(
                                    emi,
                                    lang
                                )}
                            </h6>

                            <p>
                                <Trans
                                    i18nKey="emi_interest_tenure"
                                    values={{ interest: currentInterest, tenure: currentTenure }}
                                >
                                    at {{ interest: currentInterest }}% for {{ tenure: currentTenure }} years
                                </Trans>
                            </p>
                        </div>
                    </div>

                    <div className="block">
                        <div className="emi-result">
                            <h6>
                                <span>
                                    {t("Loan (LTV)")}
                                </span>
                                {/* <em className="dirham">D</em>{" "}
                                {Math.round(totalPayable).toLocaleString()} */}
                                {formatNumber(
                                    totalPayable,
                                    lang
                                )}
                            </h6>

                            <p>
                                <Trans
                                    i18nKey="loan_ltv_used_cap"
                                    values={{
                                        used: usedLtvPercent,
                                        cap: LTV_CAP[currentBuyerType]
                                    }}
                                >
                                    Used {{ used: usedLtvPercent }}% • Cap {{ cap: LTV_CAP[currentBuyerType] }}%
                                </Trans>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="clearfix">
                <label>{t("Notes & Assumptions")}</label>

                <ul className="notes-assumptions-list">
                    <li>
                        {t(
                            "Max tenor set to 25 years. Some banks may offer shorter terms for non-residents."
                        )}
                    </li>
                    <li>
                        {t(
                            "Agency commission, bank processing and valuation fees are editable placeholders—update to your deal."
                        )}
                    </li>
                    <li>
                        {t(
                            "Upfront cash excludes developer/admin/NOC charges, service charges, and insurance (life/property), which vary by case."
                        )}
                    </li>
                    <li>
                        {t(
                            "For off-plan purchases, LTV is capped at 50% irrespective of profile."
                        )}
                    </li>
                </ul>
            </div>
        </div>
    );
};

export default MortgageCalculator;
